import React from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, TextField, Typography, Divider, Link as MuiLink, Autocomplete } from "@mui/material";
import { validatePhoneNumber } from '../hartUtils';
import { US_STATES } from 'constants/states';
import StatusField from './StatusField';

// INITIAL FORM DATA - Default values for creating a new facility
export const facilityInitialData = {
  facility_id: '',
  name: '',
  status: 'Active',
  inactive: false,
  telephone: '',
  fax: '',
  email: '',
  address_1: '',
  address_2: '',
  address_3: '',
  city: '',
  state: '',
  zip_code: '',
  contact_1_name: '',
  contact_1_phone: '',
  contact_1_email: '',
  contact_2_name: '',
  contact_2_phone: '',
  contact_2_email: '',
  notes: '',
};

// FORM VALIDATION
export function validateFacility(data) {
  const errors = {};

  // Name is required
  if (!data.name || !data.name.trim()) {
    errors.name = 'Facility Name is required';
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Validate email format if provided
  if (data.email && data.email.trim()) {
    if (!emailRegex.test(data.email)) {
      errors.email = 'Please enter a valid email address';
    }
  }

  if (data.contact_1_email && data.contact_1_email.trim()) {
    if (!emailRegex.test(data.contact_1_email)) {
      errors.contact_1_email = 'Please enter a valid email address';
    }
  }

  if (data.contact_2_email && data.contact_2_email.trim()) {
    if (!emailRegex.test(data.contact_2_email)) {
      errors.contact_2_email = 'Please enter a valid email address';
    }
  }

  const phoneError = validatePhoneNumber(data.telephone);
  if (phoneError) errors.telephone = phoneError;

  const faxError = validatePhoneNumber(data.fax);
  if (faxError) errors.fax = faxError;

  const contact1PhoneError = validatePhoneNumber(data.contact_1_phone);
  if (contact1PhoneError) errors.contact_1_phone = contact1PhoneError;

  const contact2PhoneError = validatePhoneNumber(data.contact_2_phone);
  if (contact2PhoneError) errors.contact_2_phone = contact2PhoneError;

  return errors;
}

// NULLABLE FIELDS - Fields that should be converted from empty string to null
export const facilityNullableFields = [
  'name',
  'telephone',
  'fax',
  'email',
  'address_1',
  'address_2',
  'address_3',
  'city',
  'state',
  'zip_code',
  'contact_1_name',
  'contact_1_phone',
  'contact_1_email',
  'contact_2_name',
  'contact_2_phone',
  'contact_2_email',
  'notes',
];

export const facilityPhoneFields = [
  'telephone',
  'fax',
  'contact_1_phone',
  'contact_2_phone',
];

// Simple FormField that handles both view and edit modes
const FormField = ({ label, value, editable, onChange, error, helperText, required, type, multiline, minRows, maxRows, isLink, isEmail, formatValue }) => {
  if (!editable) {
    // View mode
    const displayValue = formatValue ? formatValue(value) : (value || '—');

    if (multiline) {
      return (
        <>
          <Typography variant="body2" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
            {displayValue && displayValue !== '—' && String(displayValue).trim() ? displayValue : '—'}
          </Typography>
        </>
      );
    }

    // Render as mailto link
    if (isEmail && displayValue !== '—') {
      return (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, display: 'block', mb: 0.5 }}>
            {label}
          </Typography>
          <MuiLink
            href={`mailto:${displayValue}`}
            underline="always"
            sx={{
              color: 'secondary.main',
              textDecorationColor: (theme) => theme.palette.secondary.main,
              '&:hover': {
                color: 'primary.main',
                textDecorationColor: (theme) => theme.palette.primary.main,
              }
            }}
          >
            {displayValue}
          </MuiLink>
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        {isLink && displayValue !== '—' ? (
          <MuiLink
            href={displayValue}
            target="_blank"
            rel="noopener noreferrer"
            underline="always"
            sx={{
              wordBreak: 'break-all',
              color: 'secondary.main',
              textDecorationColor: (theme) => theme.palette.secondary.main,
              '&:hover': {
                color: 'primary.main',
                textDecorationColor: (theme) => theme.palette.primary.main,
              }
            }}
          >
            {displayValue}
          </MuiLink>
        ) : (
          <Typography variant="body1" sx={{ color: 'text.primary' }}>
            {displayValue}
          </Typography>
        )}
      </Box>
    );
  }

  // Edit mode
  return (
    <TextField
      label={label}
      value={value || ''}
      fullWidth
      required={required}
      type={type}
      multiline={multiline}
      minRows={minRows}
      maxRows={maxRows}
      onChange={onChange}
      variant="outlined"
      error={!!error}
      helperText={error || helperText || ''}
      InputLabelProps={{
        sx: { color: 'primary.main', fontWeight: 600 }
      }}
    />
  );
};

const FacilityFormFields = ({ data, editable, onChange, errors = {}, displayName, isCreating = false }) => {
  const gridSpacing = editable ? 3 : 4;
  const gridBreakpoint = editable ? 'sm' : 'md';

  return (
    <Box component={editable ? "form" : "div"} sx={{ mt: 0, mb: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="h2" sx={{ mb: 0 }}>
          {displayName} Details
        </Typography>
        <StatusField 
          data={data} 
          editable={editable} 
          onChange={onChange}
          endpoint="/hart/facility_statuses"
        />
      </Box>

      <Grid container spacing={gridSpacing}>
        {/* Row 1: Facility ID (if not creating) | Facility Name */}
        {!isCreating && (
          <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
            <FormField
              label="Facility ID"
              value={data.facility_id}
              editable={false}
              onChange={() => { }}
              helperText="System generated"
            />
          </Grid>
        )}
        <Grid item xs={12} {...{ [gridBreakpoint]: isCreating ? 12 : 6 }}>
          <FormField
            label="Facility Name"
            value={data.name}
            editable={editable}
            onChange={(e) => onChange({ ...data, name: e.target.value })}
            error={errors.name}
            required
          />
        </Grid>

        {/* Row 2: Telephone | Fax */}
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Telephone"
            value={data.telephone}
            editable={editable}
            onChange={(e) => onChange({ ...data, telephone: e.target.value })}
            type="tel"
            error={errors.telephone}
          />
        </Grid>
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Fax"
            value={data.fax}
            editable={editable}
            onChange={(e) => onChange({ ...data, fax: e.target.value })}
            type="tel"
            error={errors.fax}
          />
        </Grid>

        {/* Row 3: Email */}
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Email"
            value={data.email}
            editable={editable}
            onChange={(e) => onChange({ ...data, email: e.target.value })}
            type="email"
            error={errors.email}
            isEmail
          />
        </Grid>

        {/* Address Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: editable ? 2 : 3 }} />
        </Grid>

        {/* Row 4: Address Line 1 */}
        <Grid item xs={12}>
          <FormField
            label="Address Line 1"
            value={data.address_1}
            editable={editable}
            onChange={(e) => onChange({ ...data, address_1: e.target.value })}
          />
        </Grid>

        {/* Row 5: Address Line 2 | Address Line 3 */}
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Address Line 2"
            value={data.address_2}
            editable={editable}
            onChange={(e) => onChange({ ...data, address_2: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Address Line 3"
            value={data.address_3}
            editable={editable}
            onChange={(e) => onChange({ ...data, address_3: e.target.value })}
          />
        </Grid>

        {/* Row 6: City | State | Zip */}
        <Grid item xs={12} sm={4}>
          <FormField
            label="City"
            value={data.city}
            editable={editable}
            onChange={(e) => onChange({ ...data, city: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          {editable ? (
            <Autocomplete
              options={US_STATES}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return `${option.abbr} - ${option.name}`;
              }}
              value={US_STATES.find(s => s.abbr === data.state) || (data.state ? data.state : null)}
              onChange={(e, newValue) => {
                onChange({ ...data, state: newValue ? (typeof newValue === 'string' ? newValue : newValue.abbr) : '' });
              }}
              isOptionEqualToValue={(option, value) => {
                if (typeof value === 'string') return option.abbr === value;
                return option.abbr === value?.abbr;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State"
                  InputLabelProps={{ sx: { color: 'primary.main', fontWeight: 600 } }}
                />
              )}
            />
          ) : (
            <FormField
              label="State"
              value={data.state}
              editable={false}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormField
            label="Zip Code"
            value={data.zip_code}
            editable={editable}
            onChange={(e) => onChange({ ...data, zip_code: e.target.value })}
          />
        </Grid>

        {/* Notes (full width) */}
        <Grid item xs={12}>
          <Divider sx={{ my: editable ? 2 : 3 }} />
          <FormField
            label="Notes"
            value={data.notes}
            editable={editable}
            onChange={(e) => onChange({ ...data, notes: e.target.value })}
            multiline
            minRows={4}
            maxRows={20}
          />
        </Grid>

        {/* Contact Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: editable ? 2 : 3 }} />
          <Typography variant="h4" sx={{ mb: 2 }}>
            Contact Information
          </Typography>
        </Grid>

        {/* Contact 1 */}
        <Grid item xs={12} sm={4}>
          <FormField
            label="Contact 1 Name"
            value={data.contact_1_name}
            editable={editable}
            onChange={(e) => onChange({ ...data, contact_1_name: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormField
            label="Contact 1 Phone"
            value={data.contact_1_phone}
            editable={editable}
            onChange={(e) => onChange({ ...data, contact_1_phone: e.target.value })}
            type="tel"
            error={errors.contact_1_phone}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormField
            label="Contact 1 Email"
            value={data.contact_1_email}
            editable={editable}
            onChange={(e) => onChange({ ...data, contact_1_email: e.target.value })}
            type="email"
            error={errors.contact_1_email}
            isEmail
          />
        </Grid>

        {/* Contact 2 */}
        <Grid item xs={12} sm={4}>
          <FormField
            label="Contact 2 Name"
            value={data.contact_2_name}
            editable={editable}
            onChange={(e) => onChange({ ...data, contact_2_name: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormField
            label="Contact 2 Phone"
            value={data.contact_2_phone}
            editable={editable}
            onChange={(e) => onChange({ ...data, contact_2_phone: e.target.value })}
            type="tel"
            error={errors.contact_2_phone}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormField
            label="Contact 2 Email"
            value={data.contact_2_email}
            editable={editable}
            onChange={(e) => onChange({ ...data, contact_2_email: e.target.value })}
            type="email"
            error={errors.contact_2_email}
            isEmail
          />
        </Grid>
      </Grid>
    </Box>
  );
};

FacilityFormFields.propTypes = {
  data: PropTypes.object.isRequired,
  editable: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  displayName: PropTypes.string,
  isCreating: PropTypes.bool,
};

export default FacilityFormFields;
